import { api } from "../api/client";
import {
  arrayBufferToBase64Url,
  buildDeviceLabel,
  collectDeviceInfo,
  detectPlatform,
  urlBase64ToUint8Array
} from "./platform";

export async function ensureServiceWorker(): Promise<ServiceWorkerRegistration> {
  if (!("serviceWorker" in navigator)) {
    throw new Error("Service workers are not supported in this browser.");
  }
  return navigator.serviceWorker.register("/sw.js");
}

export async function enablePushNotifications(): Promise<void> {
  const registration = await ensureServiceWorker();
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Notification permission was not granted.");
  }

  const vapid = await api.getVapidPublicKey();
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapid.publicKey)
  });

  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
    throw new Error("Push subscription is missing endpoint or keys.");
  }

  const deviceInfo = collectDeviceInfo();
  await api.registerSubscription({
    channel: "web_push",
    endpoint: json.endpoint,
    keys: {
      p256dh: json.keys.p256dh,
      auth: json.keys.auth
    },
    origin: window.location.origin,
    platform: detectPlatform(),
    deviceLabel: buildDeviceLabel(),
    deviceInfo
  });
}

export async function getExistingPushSubscription(): Promise<PushSubscription | null> {
  if (!("serviceWorker" in navigator)) {
    return null;
  }
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
}

export function encodeKeysFromSubscription(subscription: PushSubscription): {
  p256dh: string;
  auth: string;
} {
  const p256dh = subscription.getKey("p256dh");
  const auth = subscription.getKey("auth");
  if (!p256dh || !auth) {
    throw new Error("Subscription keys missing.");
  }
  return {
    p256dh: arrayBufferToBase64Url(p256dh),
    auth: arrayBufferToBase64Url(auth)
  };
}
